import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import classnames from 'classnames';
import callOrReturn from '@adsk/bim360-matrix-react-helpers/es/callOrReturn';
import isContentOverflowingElement from '@adsk/bim360-matrix-react-helpers/es/isContentOverflowingElement';
import './_OverflowTooltip.scss';

/**
 * @class OverflowTooltip
 **/
class OverflowTooltip extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOverflowing: false
    };
    this.containerElement = null;
    this.elementWidth = 0;
    this.setContainerElement = this.setContainerElement.bind(this);
    this.setResizeObserver = this.setResizeObserver.bind(this);
  }

  componentDidMount() {
    this.setState({ isOverflowing: isContentOverflowingElement(this.containerElement) });
    this.setResizeObserver();
  }

  componentWillUnmount() {
    this.resizeObserver && this.resizeObserver.disconnect();
  }

  setResizeObserver() {
    if (this.resizeObserver || !this.containerElement) {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries, observer) => {
      const entry = entries[0];
      if (entry) {
        const curWidth = entry.contentRect.width;
        if (this.elementWidth !== curWidth && !this.state.isOverflowing) {
          this.elementWidth = curWidth;
          this.setState({ isOverflowing: isContentOverflowingElement(this.containerElement) });
        }
      }
    });
    this.elementWidth = this.containerElement.clientWidth;
    this.resizeObserver.observe(this.containerElement);
  }

  setContainerElement(el) {
    this.containerElement = el;
    this.setResizeObserver();
  }
  render() {
    const { children, content, autoTipOnEllipsis, singleLine, lineClamp, ...rest } = this.props;
    const { isOverflowing } = this.state;
    const showProps = autoTipOnEllipsis && !isOverflowing ? { show: false } : {};
    return (
      <div ref={this.setContainerElement} className="OverflowTooltip">
        <Tooltip {...rest} {...showProps} content={content || children}>
          {({ getContainerProps }) => {
            const className = classnames('OverflowTooltip__target', {
              'OverflowTooltip__target--ellipsis': isOverflowing,
              'OverflowTooltip__target--single-line': singleLine
            });
            return (
              <div
                {...getContainerProps({
                  className,
                  children: callOrReturn(children)
                })}
                style={
                  !singleLine
                    ? {
                      display: '-webkit-box',
                      WebkitLineClamp: lineClamp,
                      lineClamp,
                      WebkitBoxOrient: 'vertical'
                    }
                    : undefined
                }
              />
            );
          }}
        </Tooltip>
      </div>
    );
  }
}

OverflowTooltip.propTypes = {
  ...Tooltip.propTypes,
  /**
   * This would be the content if supplied, otherwise it'll use `children` as the content inside the tooltip.
   */
  content: PropTypes.node,
  /**
   * whether to show ellipsis on overflow
   */
  autoTipOnEllipsis: PropTypes.bool,
  /**
   * whether the content should be limited to a single line
   */
  singleLine: PropTypes.bool,
  /**
   * how many lines to show until elipsis
   */
  lineClamp: PropTypes.number,
  /**
   * children node or function
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
};

OverflowTooltip.defaultProps = {
  singleLine: true,
  lineClamp: 2
};

export default OverflowTooltip;
